import type { FieldBasisView, FileInfo, RegulationPoint } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';

export const BASES_KEY = ['kb', 'field-bases'];
const TEMPLATES_KEY = ['kb', 'regulation-templates'];

interface TemplateState {
  id: string;
  title: string;
  summary: string;
  points: number;
  taken: boolean;
}
interface ArticleItem {
  id: string;
  number: string;
  title: string;
  section: string;
  status: string;
}

/**
 * Регламенты компании и основания полей (БЗ-03): взять готовый регламент или загрузить свой файл PDF/Word,
 * затем для каждого срока в форме выбрать регламент и пункт, на который он опирается.
 */
export function RegulationsPanel() {
  const qc = useQueryClient();
  const templates = useQuery({
    queryKey: TEMPLATES_KEY,
    queryFn: () => api<TemplateState[]>('/kb/regulations/templates'),
  });
  const articles = useQuery({
    queryKey: ['kb', 'articles', 'regulation'],
    queryFn: () => api<ArticleItem[]>('/kb/articles?section=regulation'),
  });
  const bases = useQuery({ queryKey: BASES_KEY, queryFn: () => api<FieldBasisView[]>('/kb/field-bases') });
  const fileRef = useRef<HTMLInputElement>(null);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['kb'] });
  };

  const take = useMutation({
    mutationFn: (id: string) => api(`/kb/regulations/templates/${id}`, { method: 'POST' }),
    onSuccess: () => {
      toast('Регламент добавлен как черновик: проверьте текст и опубликуйте в «Базе знаний»');
      refresh();
    },
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const title = file.name.replace(/\.[^.]+$/, '').slice(0, 280);
      const a = await api<{ id: string }>('/kb/articles', {
        method: 'POST',
        body: { section: 'regulation', title: title.length < 3 ? `Регламент ${title}` : title, body: '' },
      });
      const form = new FormData();
      form.append('entityType', 'kb_article');
      form.append('entityId', a.id);
      form.append('file', file, file.name);
      const f = await api<FileInfo>('/files', { method: 'POST', form });
      try {
        await api(`/kb/regulations/${a.id}/from-file`, { method: 'POST', body: { fileId: f.id } });
        return { text: true as const, reason: '' };
      } catch (e) {
        // Ошибка чтения (скан, большой файл) — файл и регламент остаются, текст можно вписать вручную
        return { text: false as const, reason: e instanceof Error ? e.message : '' };
      }
    },
    onSuccess: (r) => {
      toast(
        r.text
          ? 'Файл загружен, текст прочитан в черновик. Проверьте и опубликуйте в «Базе знаний»'
          : `Файл загружен, но текст не прочитан${r.reason ? `: ${r.reason}` : ''}. Впишите пункты вручную в «Базе знаний»`,
      );
      refresh();
    },
  });

  if (templates.isPending || articles.isPending || bases.isPending) return <Spinner />;
  const err = templates.error ?? articles.error ?? bases.error;
  if (err) return <ErrorBox error={err} />;
  const published = (articles.data ?? []).filter((a) => a.status === 'published');
  return (
    <Panel
      title="Регламенты и основания сроков"
      meta="БЗ-03; тексты — в «Базе знаний» → «Регламенты и приказы»"
    >
      <div className="flex flex-col gap-5">
        <section className="flex flex-col gap-2">
          <h4 className="text-[13px] font-semibold">Откуда взять регламент</h4>
          <p className="text-[12px] text-ink-2">
            Готовые регламенты — заготовки: подгоните тексты и сроки под компанию и опубликуйте. Свой
            регламент или приказ можно загрузить файлом PDF или Word — текст прочитается, пункты станут
            доступны для выбора.
          </p>
          <ul className="flex flex-col gap-2">
            {(templates.data ?? []).map((t) => (
              <li
                key={t.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-line p-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium">{t.title}</div>
                  <div className="text-[12px] text-ink-2">
                    {t.summary} · пунктов: {t.points}
                  </div>
                </div>
                <Button
                  size="sm"
                  disabled={t.taken}
                  loading={take.isPending && take.variables === t.id}
                  onClick={() => take.mutate(t.id)}
                >
                  {t.taken ? 'Уже добавлен' : 'Добавить'}
                </Button>
              </li>
            ))}
          </ul>
          <div>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.docx,.txt"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) upload.mutate(f);
              }}
            />
            <Button variant="secondary" loading={upload.isPending} onClick={() => fileRef.current?.click()}>
              Загрузить свой регламент (PDF, Word)
            </Button>
          </div>
        </section>

        <section className="flex flex-col gap-2">
          <h4 className="text-[13px] font-semibold">На что опирается срок в форме</h4>
          {published.length === 0 ? (
            <p className="text-[12px] text-ink-2">
              Опубликованных регламентов пока нет: добавьте готовый или загрузите свой и опубликуйте его в
              «Базе знаний».
            </p>
          ) : null}
          <ul className="flex flex-col gap-3">
            {(bases.data ?? []).map((b) => (
              <BasisRow key={b.fieldKey} basis={b} published={published} onChanged={refresh} />
            ))}
          </ul>
        </section>
      </div>
    </Panel>
  );
}

function BasisRow({
  basis,
  published,
  onChanged,
}: {
  basis: FieldBasisView;
  published: ArticleItem[];
  onChanged: () => void;
}) {
  const [articleId, setArticleId] = useState(basis.articleId ?? '');
  const [point, setPoint] = useState(basis.point ?? '');
  const points = useQuery({
    queryKey: ['kb', 'regulation-points', articleId],
    queryFn: () => api<RegulationPoint[]>(`/kb/regulations/${articleId}/points`),
    enabled: Boolean(articleId),
  });
  const save = useMutation({
    mutationFn: () =>
      api(`/kb/field-bases/${basis.fieldKey}`, { method: 'PUT', body: { articleId, point: point || null } }),
    onSuccess: () => {
      toast('Основание сохранено');
      onChanged();
    },
  });
  const clear = useMutation({
    mutationFn: () => api(`/kb/field-bases/${basis.fieldKey}`, { method: 'DELETE' }),
    onSuccess: () => {
      setArticleId('');
      setPoint('');
      onChanged();
    },
  });
  return (
    <li className="flex flex-col gap-2 rounded-md border border-line p-2">
      <div>
        <div className="text-[13px] font-medium">{basis.label}</div>
        <div className="text-[12px] text-ink-2">{basis.what}</div>
        {basis.archived ? (
          <div className="text-[12px] text-ink-2">
            Регламент в архиве: в формах основание не показывается. Выберите другой.
          </div>
        ) : null}
      </div>
      <div className="grid gap-2 md:grid-cols-2">
        <Field label="Регламент" htmlFor={`b-${basis.fieldKey}`}>
          <Select
            id={`b-${basis.fieldKey}`}
            value={articleId}
            onChange={(e) => {
              setArticleId(e.target.value);
              setPoint('');
            }}
          >
            <option value="">— не выбран —</option>
            {published.map((a) => (
              <option key={a.id} value={a.id}>
                {a.number} {a.title}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Пункт" htmlFor={`p-${basis.fieldKey}`}>
          <Select
            id={`p-${basis.fieldKey}`}
            value={point}
            disabled={!articleId}
            onChange={(e) => setPoint(e.target.value)}
          >
            <option value="">весь регламент</option>
            {(points.data ?? []).map((p) => (
              <option key={p.code} value={p.code}>
                п. {p.code} {p.text.slice(0, 60)}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={!articleId} loading={save.isPending} onClick={() => save.mutate()}>
          Сохранить
        </Button>
        {basis.articleId ? (
          <Button size="sm" variant="ghost" loading={clear.isPending} onClick={() => clear.mutate()}>
            Убрать
          </Button>
        ) : null}
      </div>
    </li>
  );
}
